import { AnimatePresence, motion } from "framer-motion"
import { usePage } from "@inertiajs/react"
import { FileJson, StopCircle, X } from "lucide-react"
import { useMemo } from "react"
import { cancelGenerationJob } from "@/components/jobs/cancel-generation-job"
import { JobElapsedTimer, jobKindLabel, jobStatusLabel, statusDetailLabel } from "@/components/jobs/job-elapsed-timer"
import { JobSubjectLabel } from "@/components/jobs/job-subject-label"
import { useProgressClock } from "@/hooks/use-progress-clock"
import { useTranslation } from "@/hooks/use-translation"
import { canCancelExportJob, type BillingExportAccess } from "@/lib/billing-exports"
import { estimatedJobProgress, isEstimatedGenerationPhase } from "@/lib/job-estimated-progress"
import { cn } from "@/lib/utils"
import { useUiStore } from "@/stores/ui-store"
import { useWorkspaceJobsStore } from "@/stores/workspace-jobs-store"
import type { GenerationJobRow } from "@/types/workspace"

function selectActiveJobs(jobs: GenerationJobRow[]) {
  return jobs.filter((j) => j.status === "queued" || j.status === "processing")
}

function JobRequestLogButton({
  jobId,
  jobLabel,
  shotIds,
  onOpen,
}: {
  jobId: number
  jobLabel: string
  shotIds: number[]
  onOpen: (payload: { jobId: number; jobLabel: string; shotIds: number[] }) => void
}) {
  const { t } = useTranslation("jobs")
  return (
    <button
      type="button"
      title={t("showRequestPayload")}
      className="text-ui-text-muted transition-colors hover:text-ui-link"
      onClick={() => onOpen({ jobId, jobLabel, shotIds })}
    >
      <FileJson className="h-3.5 w-3.5" />
    </button>
  )
}

type ActiveJobsListProps = {
  projectId: number
  jobs: GenerationJobRow[]
  theme: string
  variant: "drawer" | "floater"
  billingExportAccess: BillingExportAccess
  onOpenPayload: (payload: { jobId: number; jobLabel: string; shotIds: number[] }) => void
}

export function ActiveJobsList({
  projectId,
  jobs,
  theme,
  variant,
  billingExportAccess,
  onOpenPayload,
}: ActiveJobsListProps) {
  const { t } = useTranslation(["layout", "jobs"])
  const isSuperAdmin =
    usePage<{ auth?: { user?: { super_admin?: boolean } | null } }>().props.auth?.user?.super_admin === true
  const now = useProgressClock(jobs.some(isEstimatedGenerationPhase))

  return (
    <ul className="space-y-2">
      {jobs.map((j) => {
        const statusLabel = jobStatusLabel(j.status)
        const statusTone = j.status === "processing" ? "text-ui-accent-text" : "text-ui-text-muted"
        const detailLabel = j.status_detail ? statusDetailLabel(j.status_detail) : null
        const isExport = j.billing_export_id != null
        const showStop = isExport ? canCancelExportJob(j, billingExportAccess) : isSuperAdmin
        const progress = estimatedJobProgress(j, now)

        return (
          <li
            key={j.id}
            className={cn(
              "rounded-lg border px-3 py-2 text-xs",
              variant === "drawer"
                ? theme === "dark"
                  ? "border-ui-accent/20 bg-ui-accent/5"
                  : "dark:border-violet-200 dark:bg-violet-50/80 light:border-ui-border light:bg-clay-1"
                : theme === "dark"
                  ? "border-ui-border bg-ui-surface-raised"
                  : "border-ui-border bg-ui-surface-sunken",
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1 overflow-hidden">
                <JobSubjectLabel job={j} projectId={projectId} />
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                <span className={cn("font-medium", statusTone)}>{statusLabel}</span>
                {isExport ? null : (
                  <JobRequestLogButton
                    jobId={j.id}
                    jobLabel={j.subject_label || jobKindLabel(j.job_kind)}
                    shotIds={j.shot_ids ?? []}
                    onOpen={onOpenPayload}
                  />
                )}
                {showStop ? (
                  <button
                    type="button"
                    title={isExport ? t("jobs:stopExport") : t("jobs:stopGeneration")}
                    className="text-ui-text-secondary transition-colors hover:text-ui-status-failed"
                    onClick={() =>
                      void cancelGenerationJob(
                        projectId,
                        j.id,
                        isExport ? t("jobs:stopExportConfirm") : t("jobs:stopGenerationConfirm"),
                      )
                    }
                  >
                    <StopCircle className="h-3.5 w-3.5" />
                  </button>
                ) : null}
              </div>
            </div>

            {detailLabel && <p className={cn("mt-1 text-[10px]", "text-ui-text-secondary")}>{detailLabel}…</p>}

            <div
              className={cn(
                "h-1.5 overflow-hidden rounded-full",
                detailLabel ? "mt-0.5" : "mt-1",
                theme === "dark" ? "bg-ui-surface-raised" : "bg-ui-surface-sunken",
              )}
            >
              <div
                className="jobs-progress-bar-animated h-full rounded-full bg-ui-accent transition-[width] duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>

            {j.started_at && (
              <div className="mt-1">
                <JobElapsedTimer startedAt={j.started_at} active />
              </div>
            )}
          </li>
        )
      })}
    </ul>
  )
}

type WorkspaceDrawerActiveJobsProps = {
  projectId: number
  theme: string
  billingExportAccess: BillingExportAccess
  onOpenPayload: (payload: { jobId: number; jobLabel: string; shotIds: number[] }) => void
}

/** Drawer “active” block — own jobs subscription so AppShell layout stays calm. */
export function WorkspaceDrawerActiveJobs({
  projectId,
  theme,
  billingExportAccess,
  onOpenPayload,
}: WorkspaceDrawerActiveJobsProps) {
  const { t } = useTranslation(["layout", "jobs"])
  // Select stable `jobs` ref — never return a fresh .filter() array from the zustand selector
  // (useSyncExternalStore loops when getSnapshot is not referentially stable).
  const jobs = useWorkspaceJobsStore((s) => s.jobs)
  const activeJobs = useMemo(() => selectActiveJobs(jobs), [jobs])

  if (activeJobs.length === 0) return null

  return (
    <div>
      <p className="mb-2 text-[10px] font-semibold tracking-wide text-ui-text-muted uppercase">{t("jobs:active")}</p>
      <ActiveJobsList
        projectId={projectId}
        jobs={activeJobs}
        theme={theme}
        variant="drawer"
        billingExportAccess={billingExportAccess}
        onOpenPayload={onOpenPayload}
      />
    </div>
  )
}

type WorkspaceJobsFloaterProps = {
  projectId: number | undefined
  theme: string
  billingExportAccess: BillingExportAccess
  onOpenPayload: (payload: { jobId: number; jobLabel: string; shotIds: number[] }) => void
}

/** Floating queue panel — own jobs subscription; unmount does not re-render AppShell chrome. */
export function WorkspaceJobsFloater({
  projectId,
  theme,
  billingExportAccess,
  onOpenPayload,
}: WorkspaceJobsFloaterProps) {
  const { t } = useTranslation(["layout", "jobs"])
  const jobsOpen = useUiStore((s) => s.jobsOpen)
  const setJobsOpen = useUiStore((s) => s.setJobsOpen)
  const jobs = useWorkspaceJobsStore((s) => s.jobs)
  const activeJobs = useMemo(() => selectActiveJobs(jobs), [jobs])
  const hasActiveJobs = activeJobs.length > 0

  return (
    <AnimatePresence>
      {jobsOpen && hasActiveJobs && projectId ? (
        <motion.div
          initial={{ x: 40, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: 40, opacity: 0 }}
          className={cn(
            "fixed right-6 bottom-6 z-50 max-h-[50vh] w-80 overflow-auto rounded-lg border p-4 shadow-2xl",
            theme === "dark"
              ? "border-ui-border dark:bg-zinc-950/95 light:bg-ui-bg-elevated/95"
              : "border-ui-border bg-ui-bg-elevated",
          )}
        >
          <div className="mb-3 flex items-center justify-between gap-2">
            <div className="text-xs font-semibold tracking-wide text-ui-text-muted uppercase">
              {t("jobs:queueTitle")}
            </div>
            <button
              type="button"
              className={cn(
                "shrink-0 rounded-lg p-1 transition-colors",
                theme === "dark"
                  ? "text-ui-text-muted hover:bg-ui-surface-raised hover:text-ui-text-secondary"
                  : "text-ui-text-muted hover:bg-ui-surface-sunken hover:text-ui-text-primary",
              )}
              aria-label={t("shell.drawer.closePanel")}
              onClick={() => setJobsOpen(false)}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <ActiveJobsList
            projectId={projectId}
            jobs={activeJobs}
            theme={theme}
            variant="floater"
            billingExportAccess={billingExportAccess}
            onOpenPayload={onOpenPayload}
          />
        </motion.div>
      ) : null}
    </AnimatePresence>
  )
}

export { JobRequestLogButton }
